import { Stack, Group, Text, Badge, Divider } from "@mantine/core";
import { getFragmentCountLabel } from "../../../utils/fragmentCount";
import type { FragmentCountQualifier } from "../../../utils/fragmentCount";

interface SpecimenGrossingData {
  specimenId: string;
  grossCode: string;
  grossDescription: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  variableValues?: Record<string, string>;
}

interface GrossingWidgetProps {
  caseData: any;
  grossingDataMap: Map<string, SpecimenGrossingData>;
}

function stripHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

function resolveAndStrip(
  html: string,
  vars: Record<string, string> | undefined
): string {
  if (!html) return "";
  let resolved = html;

  // First: extract values from data-variable-value attributes (persisted in DB)
  resolved = resolved.replace(
    /<span[^>]*data-variable-value="([^"]*)"[^>]*>.*?<\/span>/gi,
    "$1"
  );

  // Second: replace remaining {{VAR}} tokens from live vars dict
  if (vars) {
    resolved = resolved.replace(
      /\{\{([^}]+)\}\}/g,
      (_, key) => vars[key] ?? ""
    );
  }

  // Strip any remaining <span data-variable> wrappers without values
  resolved = resolved.replace(
    /<span[^>]*data-variable[^>]*>(.*?)<\/span>/gi,
    "$1"
  );

  return stripHtml(resolved).trim();
}

export function GrossingWidget({
  caseData,
  grossingDataMap,
}: GrossingWidgetProps) {
  const specimens: any[] = caseData?.specimens ?? [];

  if (specimens.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No specimens
      </Text>
    );
  }

  const sorted = [...specimens].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );

  return (
    <Stack gap={0}>
      {sorted.map((specimen: any, idx: number) => {
        // Prefer live grossingDataMap (user edits this session), fall back to API data
        const grossData = grossingDataMap.get(specimen.id);
        const grossDescription =
          grossData?.grossDescription ?? specimen.gross ?? "";
        const plainText = resolveAndStrip(
          grossDescription,
          grossData?.variableValues
        );
        const fragmentCount =
          grossData?.fragmentCount !== undefined
            ? grossData.fragmentCount
            : specimen.fragmentCount;
        const fragmentCountQualifier =
          grossData?.fragmentCountQualifier !== undefined
            ? grossData.fragmentCountQualifier
            : specimen.fragmentCountQualifier;
        const fragmentCountLabel = getFragmentCountLabel(
          fragmentCount,
          fragmentCountQualifier
        );
        const blocks: any[] = [...(specimen.blocks || [])].sort(
          (a: any, b: any) => (a.number || "").localeCompare(b.number || "")
        );
        const totalSlides = blocks.reduce(
          (sum: number, b: any) => sum + Number(b.slides?.length || 0),
          0
        );

        return (
          <Stack key={specimen.id} gap={0}>
            {idx > 0 && <Divider my="xs" />}

            {/* Specimen header */}
            <Group gap="xs" wrap="nowrap" mb={6}>
              <Badge
                size="sm"
                variant="dot"
                color="orange"
                style={{ flexShrink: 0 }}
              >
                {specimen.number || "?"}
              </Badge>
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text size="xs" fw={600} truncate>
                  {[specimen.source?.name, specimen.subsite?.name]
                    .filter(Boolean)
                    .join(", ") || "Unknown source"}
                </Text>
                {specimen.collectionMethod?.name && (
                  <Text size="xs" c="dimmed">
                    ({specimen.collectionMethod.name})
                  </Text>
                )}
                {specimen.material && (
                  <Text size="xs" c="dimmed">
                    {(
                      {
                        formalin_jar: "Formalin jar",
                        block: "Block",
                        slides_only: "Slides only",
                        blood_tube: "Blood tube",
                        urine_sample: "Urine sample",
                        stool_collection: "Stool collection",
                      } as Record<string, string>
                    )[specimen.material] || specimen.material}
                  </Text>
                )}
              </Stack>
              {fragmentCountLabel && (
                <Badge
                  size="xs"
                  variant="light"
                  color="yellow"
                  styles={{
                    label: {
                      color: "var(--mantine-color-dark-7)",
                      fontWeight: 700,
                    },
                  }}
                  style={{ flexShrink: 0 }}
                >
                  {fragmentCountLabel}
                </Badge>
              )}
              {totalSlides > 0 && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ flexShrink: 0 }}
                  title={`${blocks.length} block${
                    blocks.length !== 1 ? "s" : ""
                  }, ${totalSlides} slide${totalSlides !== 1 ? "s" : ""}`}
                >
                  {blocks.length}B · {totalSlides}S
                </Text>
              )}
            </Group>

            {/* Gross description — full text, no truncation */}
            {plainText ? (
              <Text size="xs" c="dimmed" pl={4} style={{ lineHeight: 1.5 }}>
                {plainText}
              </Text>
            ) : (
              <Text size="xs" c="dimmed" pl={4} fs="italic">
                No gross description recorded
              </Text>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
